/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from "@/assets/images/logo.png";
import cmaBanner from "@/assets/images/CMABanner.jpg";
import cmaBannerMobile from "@/assets/images/CMABanner-mobile.jpg";
import pianoKeysHand from "@/assets/images/piano-keys-hand.jpg";
import eastmanSaxophones from "@/assets/images/eastman-saxophones.jpg";
import musicTogetherIllustration from "@/assets/images/music-together-illustration.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  "/images/logo.png": logo,
  "/assets/images/logo.png": logo,
  "//images.squarespace-cdn.com/content/v1/5c7459e294d71afc38ad68b4/4004df77-de06-4741-8b58-6375f2f7ea42/WebLogoCropped.png": logo,
  "/images/CMABanner.jpg": cmaBanner,
  "/assets/images/CMABanner.jpg": cmaBanner,
  "/images/CMABanner-mobile.jpg": cmaBannerMobile,
  "/assets/images/CMABanner-mobile.jpg": cmaBannerMobile,
  "https://images.squarespace-cdn.com/content/v1/5c7459e294d71afc38ad68b4/1623198977449-IJIZ2E0YQEZVPC9R90YP/CMABanner.jpg": cmaBanner,
  // Services section images
  "/images/piano-keys-hand.jpg": pianoKeysHand,
  "/assets/images/piano-keys-hand.jpg": pianoKeysHand,
  "https://images.squarespace-cdn.com/content/v1/5c7459e294d71afc38ad68b4/49454ade-290f-47fd-9938-2eca6e4a4e4a/hand.jpg": pianoKeysHand,
  "/images/eastman-saxophones.jpg": eastmanSaxophones,
  "/assets/images/eastman-saxophones.jpg": eastmanSaxophones,
  "https://images.squarespace-cdn.com/content/v1/5c7459e294d71afc38ad68b4/c3854f0c-fbe4-489e-9b29-fa2b61426bb1/Eastman.jpg": eastmanSaxophones,
  "/images/music-together-illustration.jpg": musicTogetherIllustration,
  "/assets/images/music-together-illustration.jpg": musicTogetherIllustration,
  "https://images.squarespace-cdn.com/content/v1/5c7459e294d71afc38ad68b4/298e56f3-1b07-452b-a3aa-c02807a36d32/MT-SocialTiles_FLUTE_Illustration-06.jpg": musicTogetherIllustration,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
